import { useState, useEffect, useCallback, useRef } from "react";

// CP Settings UI modes
export type CpMode = "simple-limits" | "target-tolerance" | "custom";
export type SimpleLimitType = "minimum" | "maximum" | "range";
export type AdvancedToleranceType = "absolute" | "relative";

// UI state for each mode
export interface SimpleLimitsState {
  limitType: SimpleLimitType;
  minValue: string;
  maxValue: string;
}

export interface TargetToleranceState {
  target: string;
  tolerance: string;
  toleranceType: AdvancedToleranceType;
}

export interface AdvancedState {
  target: string;
  toleranceType: AdvancedToleranceType;
  absLower: string;
  absUpper: string;
  relLower: string;
  relUpper: string;
}

// Database field mapping (matches existing schema)
export interface CpDatabaseFields {
  cp_default_number_target?: string | null;
  cp_default_number_abs_tol_lower?: string | null;
  cp_default_number_abs_tol_upper?: string | null;
  cp_default_number_rel_tol_lower?: string | null;
  cp_default_number_rel_tol_upper?: string | null;
}

export interface UseCpSettingsProps {
  currentValues?: CpDatabaseFields;
  onChange: (values: CpDatabaseFields) => void;
}

export interface UseCpSettingsReturn {
  mode: CpMode;
  setMode: (mode: CpMode) => void;

  // Mode-specific states
  simpleLimits: SimpleLimitsState;
  targetTolerance: TargetToleranceState;
  advanced: AdvancedState;

  // Update functions for each mode
  updateSimpleLimits: (updates: Partial<SimpleLimitsState>) => void;
  updateTargetTolerance: (updates: Partial<TargetToleranceState>) => void;
  updateAdvanced: (updates: Partial<AdvancedState>) => void;

  // Helper functions
  hasValidSettings: boolean;
  reset: () => void;
}

export function useCpSettings({
  currentValues,
  onChange,
}: UseCpSettingsProps): UseCpSettingsReturn {
  // Track if this is the initial render to avoid calling onChange immediately
  const isInitialRender = useRef(true);

  // Determine initial mode based on current database values
  const determineInitialMode = useCallback((): CpMode => {
    if (!currentValues) return "simple-limits";

    const {
      cp_default_number_target: target,
      cp_default_number_abs_tol_lower: absLower,
      cp_default_number_abs_tol_upper: absUpper,
      cp_default_number_rel_tol_lower: relLower,
      cp_default_number_rel_tol_upper: relUpper,
    } = currentValues;

    // Check if it's a simple limit pattern
    if (
      target &&
      ((absLower && !absUpper) ||
        (!absLower && absUpper) ||
        (relLower && !relUpper) ||
        (!relLower && relUpper))
    ) {
      return "simple-limits";
    }

    // Check if it's symmetric tolerance pattern
    if (
      target &&
      ((absLower && absUpper && absLower === absUpper) ||
        (relLower && relUpper && relLower === relUpper))
    ) {
      return "target-tolerance";
    }

    // Default to custom for complex cases
    if (target || absLower || absUpper || relLower || relUpper) {
      return "custom";
    }

    return "simple-limits";
  }, [currentValues]);

  // Initialize mode state
  const [mode, setMode] = useState<CpMode>(determineInitialMode);

  // Initialize state for each mode
  const [simpleLimits, setSimpleLimits] = useState<SimpleLimitsState>(() => {
    // Parse current values into simple limits format
    if (!currentValues) {
      return { limitType: "minimum", minValue: "", maxValue: "" };
    }

    const {
      cp_default_number_target: target,
      cp_default_number_abs_tol_lower: absLower,
      cp_default_number_abs_tol_upper: absUpper,
    } = currentValues;

    if (target && absLower && !absUpper) {
      // Minimum limit: target - absLower = minimum value
      const minVal = (Number(target) - Number(absLower)).toString();
      return { limitType: "minimum", minValue: minVal, maxValue: "" };
    }

    if (target && !absLower && absUpper) {
      // Maximum limit: target + absUpper = maximum value
      const maxVal = (Number(target) + Number(absUpper)).toString();
      return { limitType: "maximum", minValue: "", maxValue: maxVal };
    }

    if (target && absLower && absUpper) {
      // Range: [target - absLower, target + absUpper]
      const minVal = (Number(target) - Number(absLower)).toString();
      const maxVal = (Number(target) + Number(absUpper)).toString();
      return { limitType: "range", minValue: minVal, maxValue: maxVal };
    }

    return { limitType: "minimum", minValue: "", maxValue: "" };
  });

  const [targetTolerance, setTargetTolerance] = useState<TargetToleranceState>(
    () => {
      if (!currentValues) {
        return { target: "", tolerance: "", toleranceType: "absolute" };
      }

      const {
        cp_default_number_target: target,
        cp_default_number_abs_tol_lower: absLower,
        cp_default_number_abs_tol_upper: absUpper,
        cp_default_number_rel_tol_lower: relLower,
        cp_default_number_rel_tol_upper: relUpper,
      } = currentValues;

      // Check for symmetric absolute tolerance
      if (target && absLower && absUpper && absLower === absUpper) {
        return {
          target: target,
          tolerance: absLower,
          toleranceType: "absolute",
        };
      }

      // Check for symmetric relative tolerance
      if (target && relLower && relUpper && relLower === relUpper) {
        return {
          target: target,
          tolerance: relLower,
          toleranceType: "relative",
        };
      }

      return {
        target: target || "",
        tolerance: "",
        toleranceType: "absolute",
      };
    },
  );

  const [advanced, setAdvanced] = useState<AdvancedState>(() => {
    if (!currentValues) {
      return {
        target: "",
        toleranceType: "absolute",
        absLower: "",
        absUpper: "",
        relLower: "",
        relUpper: "",
      };
    }

    const {
      cp_default_number_target: target,
      cp_default_number_abs_tol_lower: absLower,
      cp_default_number_abs_tol_upper: absUpper,
      cp_default_number_rel_tol_lower: relLower,
      cp_default_number_rel_tol_upper: relUpper,
    } = currentValues;

    // Determine tolerance type based on what's populated
    const hasAbsolute = absLower || absUpper;
    const hasRelative = relLower || relUpper;

    return {
      target: target || "",
      toleranceType: hasAbsolute ? "absolute" : "relative",
      absLower: absLower || "",
      absUpper: absUpper || "",
      relLower: relLower || "",
      relUpper: relUpper || "",
    };
  });

  // Convert UI state back to database fields
  const convertToDbFields = useCallback((): CpDatabaseFields => {
    switch (mode) {
      case "simple-limits":
        switch (simpleLimits.limitType) {
          case "minimum":
            // Minimum X: target=X, absLower=0, absUpper=null
            return {
              cp_default_number_target: simpleLimits.minValue || null,
              cp_default_number_abs_tol_lower: simpleLimits.minValue
                ? "0"
                : null,
              cp_default_number_abs_tol_upper: null,
              cp_default_number_rel_tol_lower: null,
              cp_default_number_rel_tol_upper: null,
            };
          case "maximum":
            // Maximum X: target=X, absLower=null, absUpper=0
            return {
              cp_default_number_target: simpleLimits.maxValue || null,
              cp_default_number_abs_tol_lower: null,
              cp_default_number_abs_tol_upper: simpleLimits.maxValue
                ? "0"
                : null,
              cp_default_number_rel_tol_lower: null,
              cp_default_number_rel_tol_upper: null,
            };
          case "range":
            if (simpleLimits.minValue && simpleLimits.maxValue) {
              // Range [min, max]: target = (min+max)/2, tolerances calculated
              const min = Number(simpleLimits.minValue);
              const max = Number(simpleLimits.maxValue);
              const target = (min + max) / 2;
              const lowerTol = target - min;
              const upperTol = max - target;

              return {
                cp_default_number_target: target.toString(),
                cp_default_number_abs_tol_lower: lowerTol.toString(),
                cp_default_number_abs_tol_upper: upperTol.toString(),
                cp_default_number_rel_tol_lower: null,
                cp_default_number_rel_tol_upper: null,
              };
            }
            break;
        }
        break;

      case "target-tolerance":
        if (targetTolerance.target && targetTolerance.tolerance) {
          if (targetTolerance.toleranceType === "absolute") {
            return {
              cp_default_number_target: targetTolerance.target,
              cp_default_number_abs_tol_lower: targetTolerance.tolerance,
              cp_default_number_abs_tol_upper: targetTolerance.tolerance,
              cp_default_number_rel_tol_lower: null,
              cp_default_number_rel_tol_upper: null,
            };
          } else {
            return {
              cp_default_number_target: targetTolerance.target,
              cp_default_number_abs_tol_lower: null,
              cp_default_number_abs_tol_upper: null,
              cp_default_number_rel_tol_lower: targetTolerance.tolerance,
              cp_default_number_rel_tol_upper: targetTolerance.tolerance,
            };
          }
        }
        break;

      case "custom":
        if (advanced.toleranceType === "absolute") {
          return {
            cp_default_number_target: advanced.target || null,
            cp_default_number_abs_tol_lower: advanced.absLower || null,
            cp_default_number_abs_tol_upper: advanced.absUpper || null,
            cp_default_number_rel_tol_lower: null,
            cp_default_number_rel_tol_upper: null,
          };
        } else {
          return {
            cp_default_number_target: advanced.target || null,
            cp_default_number_abs_tol_lower: null,
            cp_default_number_abs_tol_upper: null,
            cp_default_number_rel_tol_lower: advanced.relLower || null,
            cp_default_number_rel_tol_upper: advanced.relUpper || null,
          };
        }
    }

    // Default empty state
    return {
      cp_default_number_target: null,
      cp_default_number_abs_tol_lower: null,
      cp_default_number_abs_tol_upper: null,
      cp_default_number_rel_tol_lower: null,
      cp_default_number_rel_tol_upper: null,
    };
  }, [mode, simpleLimits, targetTolerance, advanced]);

  // Update functions
  const updateSimpleLimits = useCallback(
    (updates: Partial<SimpleLimitsState>) => {
      setSimpleLimits((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const updateTargetTolerance = useCallback(
    (updates: Partial<TargetToleranceState>) => {
      setTargetTolerance((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const updateAdvanced = useCallback((updates: Partial<AdvancedState>) => {
    setAdvanced((prev) => ({ ...prev, ...updates }));
  }, []);

  // Trigger onChange when state changes (but avoid infinite loops and initial render)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    const dbFields = convertToDbFields();
    onChange(dbFields);
  }, [mode, simpleLimits, targetTolerance, advanced, convertToDbFields]); // Removed onChange from deps to prevent loops

  // Check if current settings are valid
  const hasValidSettings = useCallback((): boolean => {
    switch (mode) {
      case "simple-limits":
        switch (simpleLimits.limitType) {
          case "minimum":
            return (
              !!simpleLimits.minValue && !isNaN(Number(simpleLimits.minValue))
            );
          case "maximum":
            return (
              !!simpleLimits.maxValue && !isNaN(Number(simpleLimits.maxValue))
            );
          case "range":
            return (
              !!simpleLimits.minValue &&
              !!simpleLimits.maxValue &&
              !isNaN(Number(simpleLimits.minValue)) &&
              !isNaN(Number(simpleLimits.maxValue)) &&
              Number(simpleLimits.minValue) < Number(simpleLimits.maxValue)
            );
        }
        break;
      case "target-tolerance":
        return (
          !!targetTolerance.target &&
          !!targetTolerance.tolerance &&
          !isNaN(Number(targetTolerance.target)) &&
          !isNaN(Number(targetTolerance.tolerance)) &&
          Number(targetTolerance.tolerance) >= 0
        );
      case "custom":
        const hasTarget = !!advanced.target && !isNaN(Number(advanced.target));
        const hasValidTolerances =
          advanced.toleranceType === "absolute"
            ? advanced.absLower || advanced.absUpper
            : advanced.relLower || advanced.relUpper;
        return hasTarget && !!hasValidTolerances;
    }
    return false;
  }, [mode, simpleLimits, targetTolerance, advanced]);

  // Reset to initial state
  const reset = useCallback(() => {
    setMode("simple-limits");
    setSimpleLimits({ limitType: "minimum", minValue: "", maxValue: "" });
    setTargetTolerance({
      target: "",
      tolerance: "",
      toleranceType: "absolute",
    });
    setAdvanced({
      target: "",
      toleranceType: "absolute",
      absLower: "",
      absUpper: "",
      relLower: "",
      relUpper: "",
    });
  }, []);

  return {
    mode,
    setMode,
    simpleLimits,
    targetTolerance,
    advanced,
    updateSimpleLimits,
    updateTargetTolerance,
    updateAdvanced,
    hasValidSettings: hasValidSettings(),
    reset,
  };
}
